import React, { useCallback, useEffect, useState } from "react";
import { backendApiAddress } from "../backendApi";

type Announcement = {
  id: number;
  created_at: string;
  message: string;
  is_active: boolean;
};

const api = (path: string, options?: RequestInit) =>
  fetch(`${backendApiAddress}/api/admin${path}`, { credentials: "include", ...options });

function Announcements() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [message, setMessage] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);

  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sending, setSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailResult, setEmailResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await api("/announcements");
    if (!r.ok) return;
    setAnnouncements(await r.json());
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const active = announcements.find((a) => a.is_active) ?? null;

  const publish = async () => {
    if (!message.trim()) return;
    setPublishing(true);
    setBannerError(null);
    const r = await api("/announcements", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: message.trim() }),
    });
    if (!r.ok) {
      setBannerError("Could not publish banner");
    } else {
      setMessage("");
      await load();
    }
    setPublishing(false);
  };

  const clearBanner = async () => {
    await api("/announcements/deactivate", { method: "POST" });
    await load();
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) return;
    setSending(true);
    setEmailError(null);
    setEmailResult(null);
    const r = await api("/broadcast_email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subject: emailSubject.trim(), message: emailBody.trim() }),
    });
    if (!r.ok) {
      setEmailError("Could not send email");
    } else {
      const data = await r.json();
      setEmailResult(`Sending to ${data.recipient_count} knitters…`);
      setEmailSubject("");
      setEmailBody("");
    }
    setSending(false);
  };

  return (
    <div style={{ padding: 24, maxWidth: 700, margin: "0 auto", fontFamily: "sans-serif" }}>
      <h2 style={{ marginBottom: 4 }}>Oppdrag banner</h2>
      <div style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
        Shown to knitters on the Oppdrag tab in the app. Publishing this never sends an email.
      </div>

      {active ? (
        <div style={{ border: "1px solid #6b21a8", borderRadius: 10, padding: 12, marginBottom: 20, background: "#faf5ff" }}>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Active banner</div>
          <div style={{ whiteSpace: "pre-wrap", marginBottom: 10 }}>{active.message}</div>
          <button
            onClick={clearBanner}
            style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #6b21a8", background: "white", color: "#6b21a8", cursor: "pointer" }}
          >
            Clear banner
          </button>
        </div>
      ) : (
        <div style={{ color: "#999", marginBottom: 20 }}>No active banner</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          placeholder="Banner message"
          style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: 6, fontFamily: "inherit" }}
        />
      </div>

      {bannerError && <div style={{ color: "#dc2626", marginBottom: 12 }}>{bannerError}</div>}

      <button
        onClick={publish}
        disabled={publishing || !message.trim()}
        style={{ padding: "8px 16px", borderRadius: 6, background: "#6b21a8", color: "white", border: "none", cursor: "pointer", opacity: publishing || !message.trim() ? 0.5 : 1 }}
      >
        {publishing ? "Publishing…" : "Publish banner"}
      </button>

      <h3 style={{ marginTop: 24, marginBottom: 8, fontSize: 15 }}>History</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 40 }}>
        {announcements.map((a) => (
          <div key={a.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, fontSize: 13 }}>
            <div style={{ color: "#999", marginBottom: 4 }}>
              {new Date(a.created_at).toLocaleString()} {a.is_active ? "· active" : ""}
            </div>
            <div style={{ whiteSpace: "pre-wrap" }}>{a.message}</div>
          </div>
        ))}
      </div>

      <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", marginBottom: 24 }} />

      <h2 style={{ marginBottom: 4 }}>Email knitters</h2>
      <div style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
        Sends a one-off email to every knitter, right now. Separate from the banner above —
        this never shows up in the app.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
        <input
          type="text"
          value={emailSubject}
          onChange={(e) => setEmailSubject(e.target.value)}
          placeholder="Subject"
          style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: 6 }}
        />
        <textarea
          value={emailBody}
          onChange={(e) => setEmailBody(e.target.value)}
          rows={4}
          placeholder="Email body"
          style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: 6, fontFamily: "inherit" }}
        />
      </div>

      {emailError && <div style={{ color: "#dc2626", marginBottom: 12 }}>{emailError}</div>}
      {emailResult && <div style={{ color: "#16803c", marginBottom: 12 }}>{emailResult}</div>}

      <button
        onClick={sendEmail}
        disabled={sending || !emailSubject.trim() || !emailBody.trim()}
        style={{
          padding: "8px 16px",
          borderRadius: 6,
          background: "#b91c1c",
          color: "white",
          border: "none",
          cursor: "pointer",
          opacity: sending || !emailSubject.trim() || !emailBody.trim() ? 0.5 : 1,
        }}
      >
        {sending ? "Sending…" : "Send email to all knitters"}
      </button>
    </div>
  );
}

export default Announcements;
